import {
  IOverlayParameter,
  IResult,
  IHeatParameter,
  IHeatPoint,
  ILayerConfig,
  IHeatMapParams,
} from "@/types/map";
import { loadModules } from "esri-loader";
export class HeatMap {
  private static intances: Map<string, any>;
  private view!: any;
  private heatlayers: any[] = [];
  private isWatch: boolean = false;

  private constructor(view: any) {
    this.view = view;
  }

  public static getInstance(view: __esri.MapView) {
    let id = view.container.id;
    if (!HeatMap.intances) {
      HeatMap.intances = new Map();
    }
    let intance = HeatMap.intances.get(id);
    if (!intance) {
      intance = new HeatMap(view);
      HeatMap.intances.set(id, intance);
    }
    return intance;
  }

  public static destroy() {
    (HeatMap.intances as any) = null;
  }

  //删除热力图
  public async deleteHeatMap(params: ILayerConfig) {
    let labels: string[] = [];
    if (params) {
      labels = params.label
        ? params.label instanceof Array
          ? (params.label as string[]) || []
          : [params.label]
        : [];
    }
    this.clear(labels);
  }

  //清空指定热力图
  private clear(labels: string[]) {
    if (this.heatlayers) {
      for (let i = 0; i < this.heatlayers.length; i++) {
        let layer = this.heatlayers[i];
        if (
          labels === undefined ||
          labels.length == 0 ||
          labels.indexOf(layer.label) > -1
        ) {
          this.view.map.remove(layer);
          this.heatlayers.splice(i, 1);
          i--;
        }
      }
    }
  }

  //添加热力图
  public async addHeatMap(params: IHeatParameter): Promise<IResult> {
    // Create featurelayer from client-side graphics
    let label = params.label;
    if (label == undefined) {
      this.clear([]);
    } else {
      this.clear([label]);
    }

    let that = this;
    if (!this.isWatch) {
      this.view.watch("zoom", (newValue: number) => {
        that.heatlayers.forEach((layer: any) => {
          let opt = layer.heatOpt;
          layer.renderer = newValue > opt.zoom ? opt.simple : opt.heat;
        });
      });
      this.isWatch = true;
    }
    type MapModules = [
      typeof import("esri/Graphic"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/renderers/HeatmapRenderer")
    ];
    const [Graphic, FeatureLayer, HeatmapRenderer] = await (loadModules([
      "esri/Graphic",
      "esri/layers/FeatureLayer",
      "esri/renderers/HeatmapRenderer",
    ]) as Promise<MapModules>);

    let points = params.points;
    let options = params.options;
    let opacity = options.opacity || 1;
    let graphics: any[] = [];
    let fields: any[] = [];
    let objectIdField_ = "";
    let point_fields = points[0].fields; //所有要素的字段必须与第一个要素的字段相同
    //获取每个字段的类型
    for (let fieldname in point_fields) {
      //The data type of the field.
      //"small-integer"|"integer"|"single"|"double"|"long"|"string"|"date"|"oid"|"geometry"|"blob"|"raster"|"guid"|"global-id"|"xml"
      let fieldtype = "string";
      let fieldValue = point_fields[fieldname];
      //FID / ObjectID 字段应该判断为oid类型
      if (fieldname == "FID" || fieldname == "ObjectID") {
        fieldtype = "oid";
        objectIdField_ = fieldname;
      } else if (typeof fieldValue == "number") {
        fieldtype = "double";
      } else {
        //@notice 后续可支持判断其他类型
        fieldtype = "string"; //其余全为string类型
      }
      // if (fieldname == options.field) {
      //   fieldtype = "double";
      // }
      fields.push({ name: fieldname, alias: fieldname, type: fieldtype });
    }

    // 去重
    if (options.shrink == true) {
      const tmpset = new Set<string>();
      points = points.filter((point: any): boolean => {
        if (tmpset.has(JSON.stringify(point.geometry))) {
          return false;
        }
        tmpset.add(JSON.stringify(point.geometry));
        return true;
      });
    }

    graphics = points.map((point: IHeatPoint) => {
      return new Graphic({
        geometry: {
          type: "point",
          x: point.geometry.x,
          y: point.geometry.y,
        } as any,
        attributes: point.fields,
      });
    });

    //没有主键字段
    if (objectIdField_ == "") {
      //自动生成一个主键 ObjectID
      fields.push({
        name: "ObjectID",
        alias: "ObjectID",
        type: "oid",
      });
      objectIdField_ = "ObjectID";
    }

    let heatlayer = new FeatureLayer({
      source: graphics,
      fields: fields,
      objectIdField: objectIdField_,
      geometryType: "point",
    });
    heatlayer.opacity = opacity;
    let layer: any = heatlayer;
    if (label) {
      layer.label = label;
    }
    let maxzoom = options.zoom || 100;

    let colors = params.options.colors || [
      "rgb(255, 255, 255)",
      "rgb(255, 140, 0)",
      "rgb(255, 140, 0)",
      "rgb(255, 0, 0)",
    ];
    let simpleRenderer = this.getRender(options.renderer);
    let heatmapRenderer = {
      type: "heatmap",
      field: options.field,
      colorStops: this.getHeatColor(colors),
      //这两个参数不知道是什么版本的arcgis api
      minPixelIntensity: 0,
      maxPixelIntensity: options.maxValue,
      //4.8以后的版本应该使用如下参数
      // maxDensity: options.maxValue,
      // minDensity: 0,
      radius: options.radius,
    } as any;
    layer.renderer =
      this.view.zoom > maxzoom ? simpleRenderer : heatmapRenderer;

    layer.heatOpt = {
      zoom: maxzoom,
      simple: simpleRenderer,
      heat: heatmapRenderer,
    };
    this.view.map.add(layer);
    this.heatlayers.push(layer);

    return {
      status: 0,
      message: "success!",
    };
  }

  //使用featureLayer server作为数据源生成热力图
  public async addHeatMapFromServer(params: IHeatMapParams): Promise<IResult> {
    //import modules
    const [heatmapRendererCreator, FeatureLayer] = await loadModules([
      "esri/smartMapping/renderers/heatmap",
      "esri/layers/FeatureLayer",
    ]);

    //init params
    const url: any = params.url;
    const field: any = params.field;
    let label: any = params.label;
    if (typeof url != "string") {
      return {
        status: -1,
        message: "parameter error: url",
      };
    }

    //init feature layer
    let pointLayer = new FeatureLayer({
      // url to a point dataset
      url: params.url,
    });

    // visualization based on field
    let heatmapParams = {
      layer: pointLayer,
      view: this.view,
      field: field,
    };

    // when the promise resolves, apply the renderer to the layer
    await heatmapRendererCreator
      .createRenderer(heatmapParams)
      .then((response: any) => {
        pointLayer.renderer = response.renderer;
      });

    //@todo clear last heatmap before render a new heatmap

    //@todo when change the map zoom, change the render between heatmap render and point render

    //generate label if not passthrough
    if (!label) {
      label = "heatmap-" + pointLayer.id;
    }
    //store label to featurelayer(不合适的做法，如果后续arcgis新增label字段的话就gg)
    pointLayer.label = label;

    //@todo customize colorStops

    //add heatmap layer to map
    this.view.map.add(pointLayer);
    this.heatlayers.push(pointLayer);

    return {
      status: 0,
      message: "success!",
    };
  }

  //获取渲染
  private getRender(renderer: any): any {
    let newrender = renderer;
    if (newrender && newrender.symbol) {
      newrender.symbol.type = newrender.symbol.type
        .replace("esriPMS", "picture-marker")
        .replace("esriSMS", "simple-marker");
    }
    return newrender;
  }

  //获取热力图颜色
  public getHeatColor(colors: string[] | undefined): any[] {
    let obj: any = [
      { ratio: 0, color: "rgba(255, 255, 255, 0)" },
      { ratio: 0.2, color: "rgba(255, 255, 255, 1)" },
      { ratio: 0.5, color: "rgba(255, 140, 0, 1)" },
      { ratio: 0.8, color: "rgba(255, 140, 0, 1)" },
      { ratio: 1, color: "rgba(255, 0, 0, 1)" },
    ]; //默认值
    if (colors && colors.length >= 4) {
      //"rgba(30,144,255,0)","rgba(30,144,255)","rgb(0, 255, 0)","rgb(255, 255, 0)", "rgb(254,89,0)"
      let steps = [0.2, 0.5, 0.8, 1];
      let colorStops: any[] = [{ ratio: 0, color: "rgba(255, 255, 255, 0)" }];
      steps.forEach((element: number, index: number) => {
        colorStops.push({ ratio: element, color: colors[index] });
      });
      console.log(colorStops);
      return colorStops;
    }
    return obj;
  }
}
